import api from "./axiosInstance";
import { API_ENDPOINTS } from "../config/apiconfig";

/**
 * Financial Dashboard API Service
 * Handles financial summary data for dashboard
 */

const LIST_KEYS = ["content", "data", "result", "items", "records", "list", "rows", "values"];

// Different endpoints wrap their payloads differently:
//   { content: [...] }               (Spring page)
//   { success: true, data: [...] }   (custom envelope)
//   { result: { content: [...] } }
// This digs out the first array it can find.
const toList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (const key of LIST_KEYS) {
    if (key in payload) {
      const value = payload[key];
      if (Array.isArray(value)) return value;
      if (value && typeof value === "object") {
        const nested = toList(value);
        if (nested.length) return nested;
      }
    }
  }
  return [];
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Return the first defined numeric field from a row.
const firstNumber = (row, keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== "") return num(value);
  }
  return 0;
};

const firstDate = (row) =>
  row?.date ?? row?.invoiceDate ?? row?.billDate ?? row?.startOn ?? row?.createdAt ?? row?.updatedAt ?? null;

const statusOf = (row) => String(row?.status ?? "").trim().toLowerCase();

const isDraft = (row) => statusOf(row) === "draft";

const isPaid = (row) => statusOf(row) === "paid";

export const financialDashboardApi = {
  /**
   * Get financial summary for dashboard
   * Aggregates receivables, payables, sales, purchases
   */
  getFinancialSummary: async () => {
    try {
      console.log('Fetching financial summary...');
      // Fetch data in parallel; keep them independent so one failing endpoint
      // does not wipe out the other's values.
      const [invoicesResult, billsResult] = await Promise.allSettled([
        api.get(API_ENDPOINTS.INVOICE.GET_ALL, { params: { page: 0, size: 1000 } }),
        api.get(API_ENDPOINTS.BILL.GET_ALL, { params: { page: 0, size: 1000 } })
      ]);

      if (invoicesResult.status === 'rejected') {
        console.error('Invoices request failed:', invoicesResult.reason);
      }
      if (billsResult.status === 'rejected') {
        console.error('Bills request failed:', billsResult.reason);
      }

      const invoicesResponse = invoicesResult.status === 'fulfilled' ? invoicesResult.value : { data: null };
      const billsResponse = billsResult.status === 'fulfilled' ? billsResult.value : { data: null };

      console.log('Invoices response:', invoicesResponse.data);
      console.log('Bills response:', billsResponse.data);

      const invoices = toList(invoicesResponse.data);
      const bills = toList(billsResponse.data);

      console.log('Parsed invoices:', invoices.length);
      console.log('Parsed bills:', bills.length);

      // Calculate Total Receivable (unpaid, non-draft invoices)
      const outstandingInvoices = invoices.filter(inv => !isPaid(inv) && !isDraft(inv));
      const totalReceivable = outstandingInvoices.reduce(
        (sum, inv) => sum + firstNumber(inv, ["outstandingAmount", "balanceDue", "dueAmount", "total", "totalAmount", "amount"]),
        0
      );

      // Calculate overdue amount
      const overdueInvoices = invoices.filter(inv => {
        if (isPaid(inv) || isDraft(inv)) return false;
        const rawDate = inv.dueDate;
        if (!rawDate) return false;
        const dueDate = new Date(rawDate);
        if (Number.isNaN(dueDate.getTime())) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return dueDate < today;
      });
      const overdueAmount = overdueInvoices.reduce(
        (sum, inv) => sum + firstNumber(inv, ["outstandingAmount", "balanceDue", "dueAmount", "total", "totalAmount", "amount"]),
        0
      );
      const overdueCount = overdueInvoices.length;

      // Calculate Total Payable (unpaid, non-draft bills)
      const outstandingBills = bills.filter(bill => !isPaid(bill) && !isDraft(bill));
      const totalPayable = outstandingBills.reduce(
        (sum, bill) => sum + firstNumber(bill, ["balanceDue", "outstandingAmount", "dueAmount", "amount", "total", "totalAmount"]),
        0
      );
      const openBillsCount = outstandingBills.length;

      // Calculate Total Sales (from invoices - current month/year)
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const currentMonthStart = new Date(currentYear, currentMonth, 1);
      const currentMonthEnd = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59, 999);

      const parseDate = (row) => {
        const raw = firstDate(row);
        if (!raw) return null;
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? null : d;
      };

      const currentMonthInvoices = invoices.filter(inv => {
        const d = parseDate(inv);
        return d && d >= currentMonthStart && d <= currentMonthEnd;
      });
      const totalSales = currentMonthInvoices.reduce(
        (sum, inv) => sum + firstNumber(inv, ["total", "totalAmount", "amount", "grandTotal"]),
        0
      );

      // Previous month for comparison
      const prevMonthStart = new Date(currentYear, currentMonth - 1, 1);
      const prevMonthEnd = new Date(currentYear, currentMonth, 0, 23, 59, 59, 999);
      const prevMonthInvoices = invoices.filter(inv => {
        const d = parseDate(inv);
        return d && d >= prevMonthStart && d <= prevMonthEnd;
      });
      const prevMonthSales = prevMonthInvoices.reduce(
        (sum, inv) => sum + firstNumber(inv, ["total", "totalAmount", "amount", "grandTotal"]),
        0
      );
      const salesGrowth = prevMonthSales > 0 ? ((totalSales - prevMonthSales) / prevMonthSales * 100).toFixed(1) : 0;

      // Calculate Total Purchases (from bills - current month)
      const currentMonthBills = bills.filter(bill => {
        const d = parseDate(bill);
        return d && d >= currentMonthStart && d <= currentMonthEnd;
      });
      const totalPurchases = currentMonthBills.reduce(
        (sum, bill) => sum + firstNumber(bill, ["amount", "total", "totalAmount", "grandTotal"]),
        0
      );

      const prevMonthBills = bills.filter(bill => {
        const d = parseDate(bill);
        return d && d >= prevMonthStart && d <= prevMonthEnd;
      });
      const prevMonthPurchases = prevMonthBills.reduce(
        (sum, bill) => sum + firstNumber(bill, ["amount", "total", "totalAmount", "grandTotal"]),
        0
      );
      const purchasesGrowth = prevMonthPurchases > 0 ? ((totalPurchases - prevMonthPurchases) / prevMonthPurchases * 100).toFixed(1) : 0;

      // Recent invoices (last 5)
      const recentInvoices = [...invoices]
        .sort((a, b) => (parseDate(b)?.getTime() || 0) - (parseDate(a)?.getTime() || 0))
        .slice(0, 5)
        .map(inv => ({
          id: inv.id,
          invoiceNumber: inv.invoiceNumber || inv.invoiceNo || inv.orderNo || inv.number,
          customerName: inv.customer?.name || inv.customerName || inv.customer || 'Unknown',
          date: firstDate(inv),
          amount: firstNumber(inv, ["total", "totalAmount", "amount"]),
          status: inv.status,
          balanceDue: firstNumber(inv, ["outstandingAmount", "balanceDue"])
        }));

      // Recent bills (last 5)
      const recentBills = [...bills]
        .sort((a, b) => (parseDate(b)?.getTime() || 0) - (parseDate(a)?.getTime() || 0))
        .slice(0, 5)
        .map(bill => ({
          id: bill.id,
          billNumber: bill.billNumber || bill.billNo || bill.number,
          vendorName: bill.vendor?.name || bill.vendorName || bill.vendor || 'Unknown',
          date: firstDate(bill),
          amount: firstNumber(bill, ["amount", "total", "totalAmount"]),
          status: bill.status,
          balanceDue: firstNumber(bill, ["balanceDue", "outstandingAmount"])
        }));

      return {
        totalReceivable,
        overdueAmount,
        overdueCount,
        totalPayable,
        openBillsCount,
        totalSales,
        salesGrowth: Number(salesGrowth),
        totalPurchases,
        purchasesGrowth: Number(purchasesGrowth),
        recentInvoices,
        recentBills
      };
    } catch (error) {
      console.error('Failed to fetch financial summary:', error);
      return {
        totalReceivable: 0,
        overdueAmount: 0,
        overdueCount: 0,
        totalPayable: 0,
        openBillsCount: 0,
        totalSales: 0,
        salesGrowth: 0,
        totalPurchases: 0,
        purchasesGrowth: 0,
        recentInvoices: [],
        recentBills: []
      };
    }
  },

  /**
   * Get needs attention data for dashboard
   */
  getNeedsAttention: async () => {
    try {
      console.log('Fetching needs attention data...');
      const [invoicesResult, cylindersResult, challansResult] = await Promise.allSettled([
        api.get(API_ENDPOINTS.INVOICE.GET_ALL, { params: { page: 0, size: 1000 } }),
        api.get("cylinderMaster/cylinders", { params: { size: 1000 } }),
        api.get("challans/searchChallans", { params: { page: 0, size: 1000 } })
      ]);

      const invoicesResponse = invoicesResult.status === 'fulfilled' ? invoicesResult.value : { data: null };
      const cylindersResponse = cylindersResult.status === 'fulfilled' ? cylindersResult.value : { data: null };
      const challansResponse = challansResult.status === 'fulfilled' ? challansResult.value : { data: null };

      console.log('Needs attention - Invoices:', toList(invoicesResponse.data).length);
      console.log('Needs attention - Cylinders:', toList(cylindersResponse.data).length);
      console.log('Needs attention - Challans:', toList(challansResponse.data).length);

      const invoices = toList(invoicesResponse.data);
      const cylinders = toList(cylindersResponse.data);
      const challans = toList(challansResponse.data);

      // Overdue invoices
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const overdueInvoices = invoices.filter(inv => {
        if (isPaid(inv) || isDraft(inv)) return false;
        if (!inv.dueDate) return false;
        const dueDate = new Date(inv.dueDate);
        if (Number.isNaN(dueDate.getTime())) return false;
        return dueDate < today;
      });
      const overdueInvoicesCount = overdueInvoices.length;
      const overdueInvoicesAmount = overdueInvoices.reduce(
        (sum, inv) => sum + firstNumber(inv, ["outstandingAmount", "balanceDue", "total", "totalAmount", "amount"]),
        0
      );

      // Cylinders not returned (OUT_WITH_CUSTOMER or ISSUED status)
      const cylindersNotReturned = cylinders.filter(c => 
        c.currentStatus === 'OUT_WITH_CUSTOMER' || c.currentStatus === 'ISSUED'
      );
      const cylindersNotReturnedCount = cylindersNotReturned.length;
      const customersWithCylinders = new Set(cylindersNotReturned.map(c => c.customerId || c.customer?.id).filter(Boolean)).size;

      // Cylinders testing due (within 15 days)
      const fifteenDaysFromNow = new Date(today);
      fifteenDaysFromNow.setDate(today.getDate() + 15);
      const cylindersTestingDue = cylinders.filter(c => {
        if (!c.testingDate) return false;
        const testingDate = new Date(c.testingDate);
        return testingDate <= fifteenDaysFromNow && testingDate >= today;
      });
      const cylindersTestingDueCount = cylindersTestingDue.length;

      // Challans awaiting completion (open/pending status)
      const awaitingChallans = challans.filter(c => 
        c.status === 'PENDING' || c.status === 'OPEN' || c.status === 'DRAFT'
      );
      const awaitingChallansCount = awaitingChallans.length;
      const oldestAwaitingChallan = awaitingChallans.length > 0 
        ? Math.min(...awaitingChallans.map(c => {
            if (!c.date) return 999;
            const challanDate = new Date(c.date);
            return Math.floor((today - challanDate) / (1000 * 60 * 60 * 24));
          }))
        : 0;

      return {
        overdueInvoices: {
          count: overdueInvoicesCount,
          amount: overdueInvoicesAmount
        },
        cylindersNotReturned: {
          count: cylindersNotReturnedCount,
          customers: customersWithCylinders
        },
        cylindersTestingDue: {
          count: cylindersTestingDueCount
        },
        challansAwaitingCompletion: {
          count: awaitingChallansCount,
          oldestDays: oldestAwaitingChallan
        }
      };
    } catch (error) {
      console.error('Failed to fetch needs attention:', error);
      return {
        overdueInvoices: { count: 0, amount: 0 },
        cylindersNotReturned: { count: 0, customers: 0 },
        cylindersTestingDue: { count: 0 },
        challansAwaitingCompletion: { count: 0, oldestDays: 0 }
      };
    }
  },

  /**
   * Get cylinder tracking summary for dashboard
   */
  getCylinderTrackingSummary: async () => {
    try {
      console.log('Fetching cylinder tracking summary...');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];
      const fifteenDaysFromNow = new Date(today);
      fifteenDaysFromNow.setDate(today.getDate() + 15);
      
      const [productResult, cylindersResult, challansResult] = await Promise.allSettled([
        api.get("reports/products"),
        api.get("cylinderMaster/cylinders", { params: { size: 1000 } }),
        api.get("challans/searchChallans", { params: { page: 0, size: 100, sortBy: 'date', direction: 'DESC' } })
      ]);

      const productWiseReport = productResult.status === 'fulfilled' ? productResult.value : { data: null };
      const cylindersResponse = cylindersResult.status === 'fulfilled' ? cylindersResult.value : { data: null };
      const recentChallansResponse = challansResult.status === 'fulfilled' ? challansResult.value : { data: null };

      console.log('Cylinder tracking - Product report:', toList(productWiseReport.data).length);
      console.log('Cylinder tracking - Cylinders:', toList(cylindersResponse.data).length);
      console.log('Cylinder tracking - Challans:', toList(recentChallansResponse.data).length);

      const productData = toList(productWiseReport.data);
      const cylinders = toList(cylindersResponse.data);
      const challans = toList(recentChallansResponse.data);

      // Total delivered/received from product-wise report
      const totalDelivered = productData.reduce((sum, p) => sum + Number(p.deliveredQty || 0), 0);
      const totalReceived = productData.reduce((sum, p) => sum + Number(p.receivedQty || 0), 0);

      // Today's delivered/received
      const todayDeliveredChallans = challans.filter(c => c.challanType === 'DELIVERED' && c.date === todayStr);
      const todayReceivedChallans = challans.filter(c => c.challanType === 'RECEIVED' && c.date === todayStr);
      const todayCombinedChallans = challans.filter(c => c.challanType === 'COMBINED' && c.date === todayStr);
      
      const deliveredToday = todayDeliveredChallans.reduce((sum, c) => 
        sum + (c.items?.reduce((s, i) => s + Number(i.deliveredQty || 0), 0) || 0), 0);
      const receivedToday = todayReceivedChallans.reduce((sum, c) => 
        sum + (c.items?.reduce((s, i) => s + Number(i.receivedQty || 0), 0) || 0), 0);
      const combinedToday = todayCombinedChallans.reduce((sum, c) => 
        sum + (c.items?.reduce((s, i) => s + Number(i.deliveredQty || 0) + Number(i.receivedQty || 0), 0) || 0), 0);

      // Cylinder master stats
      let totalCylinders = 0;
      let availableCylinders = 0;
      let withCustomerCylinders = 0;
      let inTransitCylinders = 0;
      let testingDueCount = 0;
      
      if (cylinders.length > 0) {
        totalCylinders = cylinders.length;
        cylinders.forEach(c => {
          const status = c.currentStatus;
          if (status === 'IN_STOCK' || status === 'IN_INVENTORY') {
            availableCylinders++;
          } else if (status === 'OUT_WITH_CUSTOMER' || status === 'ISSUED') {
            withCustomerCylinders++;
          } else if (status === 'IN_TRANSIT') {
            inTransitCylinders++;
          }
          
          if (c.testingDate) {
            const testingDate = new Date(c.testingDate);
            if (testingDate <= fifteenDaysFromNow && testingDate >= today) {
              testingDueCount++;
            }
          }
        });
      } else {
        // Fallback to product-wise report
        totalCylinders = totalDelivered;
        withCustomerCylinders = totalDelivered - totalReceived;
      }

      const capacity = totalCylinders > 0 ? ((availableCylinders / totalCylinders) * 100).toFixed(0) : 0;

      return {
        deliveredCylinders: totalDelivered,
        deliveredToday: deliveredToday,
        receivedCylinders: totalReceived,
        receivedToday: receivedToday,
        combinedCylinders: totalDelivered - totalReceived, // Net with customers
        combinedToday: combinedToday,
        availableStock: availableCylinders,
        totalStock: totalCylinders,
        capacityPercentage: capacity,
        withCustomer: withCustomerCylinders,
        inTransit: inTransitCylinders,
        testingDue: testingDueCount
      };
    } catch (error) {
      console.error('Failed to fetch cylinder tracking:', error);
      return {
        deliveredCylinders: 0,
        deliveredToday: 0,
        receivedCylinders: 0,
        receivedToday: 0,
        combinedCylinders: 0,
        combinedToday: 0,
        availableStock: 0,
        totalStock: 0,
        capacityPercentage: 0,
        withCustomer: 0,
        inTransit: 0,
        testingDue: 0
      };
    }
  }
};

export default financialDashboardApi;